<template>
  <div class="assistant-chat assistant-thread" data-testid="assistant-chat">
    <!-- Display-only toggle for the reasoning timeline (reasoning is received
         regardless; this just shows/hides the reason segments). Default off. -->
    <div
      v-if="activeChatId !== null"
      class="assistant-toolbar"
      data-testid="assistant-toolbar"
    >
      <input-toggle
        data-testid="assistant-reasoning-toggle"
        v-model="showReasoning"
        elem-id="assistant_show_reasoning"
        :label="t('Exibir raciocínio')"
      />
    </div>

    <!-- Message list (presentational bubbles) -->
    <div ref="messagesContainer" class="assistant-messages" data-testid="assistant-messages">
      <p
        v-if="activeChatId === null"
        class="assistant-empty"
        data-testid="assistant-empty"
      >
        {{ t('Selecione ou crie uma conversa para começar') }}
      </p>
      <p
        v-else-if="messages.length === 0"
        class="assistant-empty"
        data-testid="assistant-empty"
      >
        {{ t('Pergunte sobre clientes e ordens de serviço') }}
      </p>

      <div
        v-for="(message, index) in messages"
        :key="message.id"
        class="assistant-bubble-wrapper"
        :class="message.role === 'user' ? 'assistant-bubble-wrapper-user' : 'assistant-bubble-wrapper-assistant'"
      >
        <div
          class="assistant-bubble"
          :class="message.role === 'user' ? 'assistant-bubble-user' : 'assistant-bubble-assistant'"
          :data-testid="`assistant-message-${index}`"
          :data-testid-role="message.role"
        >
          <!-- User messages: plain text interpolation (no injection surface) -->
          <span
            v-if="message.role === 'user'"
            class="assistant-bubble-text"
            data-testid="assistant-message-user"
          >{{ message.content }}</span>

          <!-- Assistant messages (streamed): chronological reason/output timeline.
               reason segments are display-toggleable (still received either way);
               output segments are each their own markdown block (stacked, no run-on). -->
          <template v-if="message.role === 'assistant' && message.segments && message.segments.length">
            <template v-for="(seg, si) in message.segments" :key="si">
              <div
                v-if="seg.type === 'reason'"
                v-show="showReasoning"
                class="assistant-reason"
                data-testid="assistant-message-reason"
              >{{ seg.text }}</div>
              <div
                v-else
                class="assistant-bubble-text assistant-bubble-markdown assistant-output-segment"
                data-testid="assistant-message-assistant"
                v-html="renderMarkdownSanitizado(seg.text)"
              ></div>
            </template>
          </template>

          <!-- Assistant messages (persisted/historical): no timeline — render content. -->
          <div
            v-else-if="message.role === 'assistant'"
            class="assistant-bubble-text assistant-bubble-markdown"
            data-testid="assistant-message-assistant"
            v-html="renderMarkdownSanitizado(message.content)"
          ></div>

          <!-- Streaming progress indicator: shown while the SSE stream is open. -->
          <span
            v-if="message.streaming"
            class="assistant-streaming-indicator"
            data-testid="assistant-streaming-indicator"
            aria-label="pensando…"
          >▋</span>

          <!-- Tool-call progress: shown between tool_start and tool_result frames. -->
          <span
            v-if="message.toolActivity"
            class="assistant-tool-indicator"
            :data-testid="`assistant-tool-indicator`"
          >{{ t('chamando') }} {{ message.toolActivity.name }}…</span>
        </div>

        <!-- PII-free diagnostics, collapsible, only under assistant turns. -->
        <AssistantMessageDiagnostics
          v-if="message.role === 'assistant' && message.diagnostics !== null"
          :diagnostics="message.diagnostics"
        />
      </div>
    </div>

    <!-- Error surface -->
    <Message
      v-if="error"
      severity="error"
      :closable="false"
      class="assistant-error"
      data-testid="assistant-error"
    >
      {{ error }}
    </Message>

    <!-- Billing slot — filled by the page/modal that owns the chat shell so
         M6 can re-parent it into AssistantModal with zero churn here. -->
    <slot name="billing" />

    <!-- Input row -->
    <div class="assistant-input-row">
      <div class="assistant-input-field">
        <input-text
          data-testid="assistant-input"
          elem-id="assistant_input"
          :placeholder="t('Pergunte sobre clientes e ordens de serviço')"
          :disabled="loading || activeChatId === null"
          :fluid="true"
          v-model="draft"
          @keyup.enter="submit"
        >
        </input-text>
      </div>
      <Button
        data-testid="assistant-send-btn"
        :label="t('Enviar')"
        icon="pi pi-send"
        severity="primary"
        :loading="loading"
        :disabled="loading || activeChatId === null || draft.trim().length === 0"
        @click="submit"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AssistantMessageDiagnostics from './AssistantMessageDiagnostics.vue';
import type { StreamingChatMessageView } from '../../composables/useAgentChat';
import { renderMarkdownSanitizado } from '../../utils/renderMarkdownSanitizado';

const props = defineProps<{
  messages: StreamingChatMessageView[];
  loading: boolean;
  error: string | null;
  activeChatId: number | null;
}>();

const emit = defineEmits<{
  send: [text: string];
}>();

const { t } = useI18n();

const draft = ref<string>('');
const messagesContainer = ref<HTMLDivElement | null>(null);

// Display-only: whether to show reason segments in the timeline. The reasoning is
// streamed and stored regardless; this just toggles its visibility. Default off.
const showReasoning = ref<boolean>(false);

async function scrollToBottom(): Promise<void> {
  await nextTick();
  const el = messagesContainer.value;
  if (el) {
    el.scrollTop = el.scrollHeight;
  }
}

// Autoscroll when a new message is appended (length change).
watch(() => props.messages.length, scrollToBottom);

// Autoscroll as segments grow on the active (last/streaming) message so the bubble
// grows without losing scroll position. Reacts to a new segment OR growth of the
// last segment (covers both reason and output streaming).
watch(
  () => {
    const last = props.messages[props.messages.length - 1];
    if (!last?.streaming) return null;
    const segs = last.segments ?? [];
    const lastSeg = segs[segs.length - 1];
    return `${segs.length}:${lastSeg ? lastSeg.text.length : 0}`;
  },
  scrollToBottom,
);

function submit(): void {
  const text = draft.value.trim();
  if (!text || props.loading || props.activeChatId === null) {
    return;
  }
  emit('send', text);
  draft.value = '';
}
</script>

<style scoped>
.assistant-chat {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.assistant-messages {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 12rem;
  max-height: 60vh;
  overflow-y: auto;
  padding: 0.5rem;
}

.assistant-empty {
  color: var(--p-text-muted-color);
  text-align: center;
  margin: auto;
}

.assistant-bubble-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 80%;
}

.assistant-bubble-wrapper-user {
  align-self: flex-end;
  align-items: flex-end;
}

.assistant-bubble-wrapper-assistant {
  align-self: flex-start;
  align-items: flex-start;
}

.assistant-bubble {
  display: flex;
  padding: 0.5rem 0.75rem;
  border-radius: 0.75rem;
  word-break: break-word;
}

.assistant-bubble-user {
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
}

.assistant-bubble-assistant {
  background: var(--p-surface-200);
  color: var(--p-text-color);
  /* Stack the reason/output timeline segments vertically (the bubble is
     display:flex, whose default row direction would lay them side by side).
     `gap` separates each segment so stacked outputs never run together. */
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
}

.assistant-input-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.assistant-input-field {
  flex: 1;
}

.assistant-streaming-indicator {
  display: inline-block;
  margin-left: 2px;
  animation: blink 1s step-start infinite;
  color: inherit;
  opacity: 0.7;
}

@keyframes blink {
  50% { opacity: 0; }
}

.assistant-tool-indicator {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  font-style: italic;
}

/* Reasoning segment — a muted, italic "thinking" block marked with a left accent
   so it reads as distinct from the answer in the timeline. pre-wrap preserves the
   model's own line breaks between thoughts. */
.assistant-reason {
  font-style: italic;
  color: var(--p-text-muted-color);
  font-size: 0.85em;
  white-space: pre-wrap;
  border-left: 2px solid var(--p-surface-400, #cbd5e1);
  padding-left: 0.5rem;
  align-self: stretch;
}

/* Each output segment is its own block; the bubble's `gap` spaces them apart so
   stacked answers (reasoning hidden) never run together. */
.assistant-output-segment {
  align-self: stretch;
}

/* Reasoning display toggle, pinned above the message list. */
.assistant-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 0 0.25rem 0.25rem;
}

/* ── Assistant markdown rendering ─────────────────────────────────────────── */

/*
 * The v-html target must be display:block with min-width:0 so block-level
 * markdown elements (tables, pre, ul) lay out normally inside the flex bubble.
 * overflow-x:auto lets wide tables scroll instead of blowing out the bubble.
 */
.assistant-bubble-markdown {
  display: block;
  min-width: 0;
  overflow-x: auto;
}

/* markdown-it wraps single-line replies in <p>; remove stray margin on the
   first and last block so the bubble padding is the only breathing room. */
:deep(.assistant-bubble-markdown > p:first-child) {
  margin-top: 0;
}
:deep(.assistant-bubble-markdown > p:last-child) {
  margin-bottom: 0;
}

/* Tables */
:deep(.assistant-bubble-markdown table) {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 0.875em;
}
:deep(.assistant-bubble-markdown th),
:deep(.assistant-bubble-markdown td) {
  border: 1px solid var(--p-surface-400, #ccc);
  padding: 0.3rem 0.5rem;
  white-space: nowrap;
}
:deep(.assistant-bubble-markdown th) {
  font-weight: 600;
  background: var(--p-surface-300, #e5e7eb);
}

/* Lists */
:deep(.assistant-bubble-markdown ul),
:deep(.assistant-bubble-markdown ol) {
  padding-left: 1.4rem;
  margin: 0.25rem 0;
}
:deep(.assistant-bubble-markdown li) {
  margin: 0.1rem 0;
}

/* Inline code */
:deep(.assistant-bubble-markdown code) {
  font-family: ui-monospace, monospace;
  font-size: 0.875em;
  background: var(--p-surface-300, #e5e7eb);
  padding: 0.1em 0.3em;
  border-radius: 0.25rem;
}

/* Code blocks */
:deep(.assistant-bubble-markdown pre) {
  background: var(--p-surface-300, #e5e7eb);
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  overflow-x: auto;
  font-size: 0.875em;
  margin: 0.4rem 0;
}
:deep(.assistant-bubble-markdown pre code) {
  background: none;
  padding: 0;
}

/* Links */
:deep(.assistant-bubble-markdown a) {
  color: var(--p-primary-color);
  text-decoration: underline;
}
</style>
